import { Link } from 'react-router-dom';
import { useEffect } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { translations } from '../translations';

const AboutPage = () => {
  const { language } = useLanguage();
  const t = translations[language].aboutPage;

  useEffect(() => {
    document.title = 'Sanction Screener - About';
  }, []);

  return (
    <div>
      {/* Hero Section */}
      <section className="bg-gradient-to-r from-blue-800 to-indigo-900 text-white py-20">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-4xl md:text-5xl font-bold mb-6">{t.heroTitle}</h1>
            <p className="text-xl mb-8 text-blue-100">
              {t.heroSubtitle}
            </p>
          </div>
        </div>
      </section>

      {/* Our Story Section */}
      <section className="py-20">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="mb-16">
              <div className="inline-block bg-blue-100 text-blue-700 font-semibold px-4 py-2 rounded-full mb-4">{t.ourStory}</div>
              <h2 className="text-3xl font-bold mb-6 text-gray-800">{t.ourJourney}</h2>
              
              <div className="prose prose-lg max-w-none text-gray-600">
                <p className="mb-4">
                  {t.storyParagraph1}
                </p>
                <p className="mb-4">
                  {t.storyParagraph2}
                </p>
                <p>
                  {t.storyParagraph3}
                </p>
              </div>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-12 mb-16">
              <div>
                <div className="inline-block bg-blue-100 text-blue-700 font-semibold px-4 py-2 rounded-full mb-4">{t.ourMission}</div>
                <h2 className="text-3xl font-bold mb-6 text-gray-800">{t.whyWeExist}</h2>
                <div className="prose prose-lg max-w-none text-gray-600">
                  <p>
                    {t.missionParagraph1}
                  </p>
                  <p>
                    {t.missionParagraph2}
                  </p>
                </div>
              </div>
              <div>
                <div className="inline-block bg-blue-100 text-blue-700 font-semibold px-4 py-2 rounded-full mb-4">{t.ourVision}</div>
                <h2 className="text-3xl font-bold mb-6 text-gray-800">{t.whereWereHeaded}</h2>
                <div className="prose prose-lg max-w-none text-gray-600">
                  <p>
                    {t.visionParagraph1}
                  </p>
                  <p>
                    {t.visionParagraph2}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center mb-16">
            <div className="inline-block bg-blue-100 text-blue-700 font-semibold px-4 py-2 rounded-full mb-4">{t.ourValues}</div>
            <h2 className="text-3xl font-bold mb-6 text-gray-800">{t.whatDrivesUs}</h2>
            <p className="text-xl text-gray-600">
              {t.valuesIntro}
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-5xl mx-auto">
            <ValueCard 
              icon={
                <svg xmlns="http://www.w3.org/2000/svg" className="h-12 w-12" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                </svg>
              }
              title={t.integrity}
              description={t.integrityDesc}
            />
            <ValueCard 
              icon={
                <svg xmlns="http://www.w3.org/2000/svg" className="h-12 w-12" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                </svg>
              }
              title={t.innovation}
              description={t.innovationDesc}
            />
            <ValueCard 
              icon={
                <svg xmlns="http://www.w3.org/2000/svg" className="h-12 w-12" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                </svg>
              }
              title={t.customerFocus}
              description={t.customerFocusDesc}
            />
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-5xl mx-auto mt-8">
            <ValueCard 
              icon={
                <svg xmlns="http://www.w3.org/2000/svg" className="h-12 w-12" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19.428 15.428a2 2 0 00-1.022-.547l-2.387-.477a6 6 0 00-3.86.517l-.318.158a6 6 0 01-3.86.517L6.05 15.21a2 2 0 00-1.806.547M8 4h8l-1 1v5.172a2 2 0 00.586 1.414l5 5c1.26 1.26.367 3.414-1.415 3.414H4.828c-1.782 0-2.674-2.154-1.414-3.414l5-5A2 2 0 009 10.172V5L8 4z" />
                </svg>
              }
              title={t.excellence}
              description={t.excellenceDesc}
            />
            <ValueCard 
              icon={
                <svg xmlns="http://www.w3.org/2000/svg" className="h-12 w-12" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h.5A2.5 2.5 0 0020 5.5v-1.65" />
                </svg>
              }
              title={t.accessibility}
              description={t.accessibilityDesc}
            />
            <ValueCard 
              icon={
                <svg xmlns="http://www.w3.org/2000/svg" className="h-12 w-12" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6V4m0 2a2 2 0 100 4m0-4a2 2 0 110 4m-6 8a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4m6 6v10m6-2a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4" />
                </svg>
              }
              title={t.adaptability}
              description={t.adaptabilityDesc}
            />
          </div>
        </div>
      </section>
    </div>
  );
};

const ValueCard = ({ icon, title, description }) => {
  return (
    <div className="bg-white rounded-lg p-8 shadow-md border border-gray-100 text-center">
      <div className="text-blue-600 mb-5 flex justify-center">{icon}</div>
      <h3 className="text-xl font-bold mb-3 text-gray-800">{title}</h3>
      <p className="text-gray-600">{description}</p>
    </div>
  );
};

export default AboutPage; 